import { useLayoutEffect, useState } from "react";
import type { FichierRendu } from "./contract";

/**
 * Les fichiers d'une reponse du module Silhouettes, en liens locaux (`Blob`) : ce que la page d'une
 * silhouette (`silhouettes.tsx`) et celle du serre-livres (`silhouettes-serre-livres.tsx`) font de
 * ce que `products` leur rend en base64. Rien n'est garde ailleurs que dans la page : les liens sont
 * revoques quand la reponse est remplacee et au demontage.
 */

/** Les octets d'un fichier de la reponse, en `Blob`. */
export function blobDe(base64: string, type: string): Blob {
  const binaire = atob(base64);
  const octets = new Uint8Array(binaire.length);
  for (let i = 0; i < binaire.length; i += 1) octets[i] = binaire.charCodeAt(i);
  return new Blob([octets], { type });
}

/**
 * Fait telecharger un lien local sous un nom de fichier : un lien cache, clique par la page puis
 * retire, comme le fait la page des cadres. Le lien lui-meme reste a son proprietaire (les liens
 * d'une reponse, `useLiensDeFichiers`), qui le revoque : un lien revoque tout de suite apres le clic
 * peut couper le telechargement dans certains navigateurs.
 */
export function declencherTelechargement(href: string, nom: string): void {
  const ancre = document.createElement("a");
  ancre.href = href;
  ancre.download = nom;
  ancre.hidden = true;
  document.body.append(ancre);
  ancre.click();
  ancre.remove();
}

/**
 * Ce que les liens lisent d'une reponse : ses fichiers, et son apercu quand elle en a un. La reponse
 * d'un rendu a les deux ; celle d'un assemblage n'a pas d'apercu.
 */
type ReponseAvecFichiers = {
  fichiers: readonly FichierRendu[];
  apercu_png?: string | null | undefined;
};

/**
 * Ce que les liens fabriquent : `tout` (les fichiers de la reponse et son apercu) ou `apercu` seul.
 * Une colonne du mode serre-livres n'affiche que l'apercu de son rendu : ses 3MF et ses zip, pas
 * telechargeables dans cette page (le jeu complet vient de l'assemblage), ne sont pas decodes.
 */
type Quoi = "tout" | "apercu";

/**
 * Les liens locaux des fichiers d'une reponse, par nom de fichier, plus `apercu`. Crees a
 * chaque nouvelle reponse, revoques quand elle est remplacee et au demontage. Avant que
 * l'ecran ne se peigne (`useLayoutEffect`) : les liens s'allument avec l'apercu, pas une
 * image plus tard.
 */
export function useLiensDeFichiers(
  reponse: ReponseAvecFichiers | null,
  quoi: Quoi = "tout",
): Record<string, string> {
  const [liens, setLiens] = useState<Record<string, string>>({});
  useLayoutEffect(() => {
    if (!reponse) {
      // Le meme objet s'il est deja vide : pas de rendu de plus pour rien.
      setLiens((actuels) => (Object.keys(actuels).length === 0 ? actuels : {}));
      return;
    }
    const crees: Record<string, string> = {};
    if (quoi === "tout") {
      for (const fichier of reponse.fichiers) {
        crees[fichier.nom] = URL.createObjectURL(blobDe(fichier.contenu, fichier.type));
      }
    }
    if (reponse.apercu_png) {
      crees.apercu = URL.createObjectURL(blobDe(reponse.apercu_png, "image/png"));
    }
    setLiens(crees);
    return () => {
      for (const lien of Object.values(crees)) URL.revokeObjectURL(lien);
    };
  }, [reponse, quoi]);
  return liens;
}
